import {Grid, makeStyles} from '@yandex-delivery/blocks'
import clsx from 'clsx'
import React, {useMemo} from 'react'
import type {HorizontalTimelineItem, Timeline as TimelineType} from 'src/api/types'
import {tagToUrl} from 'src/shared/utils'

import {Validator} from './Validator'

type Props = {
  timeline: TimelineType
  showBubble?: boolean
}

export const useStyles = makeStyles(
  theme => ({
    root: {
      paddingBottom: theme.spacing(2),
    },
    borderTop: {
      borderTopColor: theme.go?.schemes?.colors?.system?.line || '#d2d0cc',
      borderTopWidth: 1,
      borderTopStyle: 'solid',
    },
    image: {
      width: 24,
      height: 24,
    },
    imageContainer: {
      backgroundColor: theme.go.schemes.colors.system.BgMinor,
      borderRadius: 100,
      padding: theme.spacing(1),
      margin: theme.spacing(0, 1),
    },
    connector: {
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        display: 'inline-block',
        left: '32px',
        height: 6,
        width: 16,
        backgroundColor: theme.go.schemes.colors.system.BgMinor,
      },
    },
    success: {
      // #FCE000 - специфичный желтый цвет для успешного состояния Яндекс.Доставки
      backgroundColor: '#FCE000',
      '&::before': {backgroundColor: '#FCE000'},
    },
    // Переходной коннектор: желтый слева переходит в серый справа (плавный градиент)
    transitionConnector: {
      '&::before': {
        background: `linear-gradient(to right, #FCE000 0%, ${theme.go.schemes.colors.system.BgMinor} 100%)`,
      },
    },
  }),
  {name: 'Timeline'},
)

const Timeline: React.FC<Props> = ({timeline, showBubble = false}) => {
  const classes = useStyles()

  const currentPointIndex = useMemo(() => {
    if (!timeline.current_item_id) return 0
    const index = timeline.horizontal.findIndex((item: HorizontalTimelineItem) => item.id === timeline.current_item_id)
    return index >= 0 ? index : 0
  }, [timeline.current_item_id, timeline.horizontal])

  return (
    <Grid className={classes.root} container direction="column">
      <Grid container justify="center">
        {timeline.horizontal.map((item, index) => {
          const isSuccess = item.status === 'passed'
          const nextItem = timeline.horizontal[index + 1]
          const isTransition = isSuccess && nextItem && nextItem.status !== 'passed'

          return (
            <Grid className={clsx(classes.imageContainer, isSuccess && classes.success)} item key={item.id}>
              <Grid
                alignItems="center"
                className={clsx(
                  isSuccess && classes.success,
                  index !== timeline.horizontal.length - 1 && classes.connector,
                  isTransition && classes.transitionConnector,
                )}
                container
                item
              >
                <img
                  alt=""
                  className={classes.image}
                  {...(!isSuccess && {'data-theme-invert': true})}
                  loading="lazy"
                  src={tagToUrl(item.image_tag)}
                />
              </Grid>
            </Grid>
          )
        })}
      </Grid>

      {timeline.bubble && showBubble && (
        <Validator
          bubble={timeline.bubble}
          currentPointIndex={currentPointIndex}
          pointCount={timeline.horizontal.length}
        />
      )}
    </Grid>
  )
}

export default Timeline
